import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { FaCheck } from 'react-icons/fa';
import { useAuth } from '../../../../contexts/AuthContext';
import handleAutofillCourse from '../../../Autofill';

const Modal = ({ isVisible, onClose, children }) => {
  if (!isVisible) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative z-50" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
};

const fetchMajorData = (fetchedMajors, major, dataType, termMap) => {
  console.log('Term Map Exists? ', termMap);
     
  try {
    if (!fetchedMajors[major]) {
      console.error('Major not found in fetchedMajors.');
      return null;
    }
    const calculateMaxYears = (courses) =>
      Math.max(...courses.map((course) => course.courseYear));
    // We'll use the provided termMap as our semesterMap
    const semesterMap = termMap;

    const majorCoursesFromBackend = Object.values(fetchedMajors[major]);
    const courseMap = {};
    const maxMajorYears = calculateMaxYears(majorCoursesFromBackend);

    // Initialize each year with keys for all dynamic terms
    for (let i = 1; i <= maxMajorYears; i++) {
      const yearKey = `year${i}`;
      courseMap[yearKey] = {};
      // For every term name in termMap (the values), initialize an empty array
      Object.values(semesterMap).forEach((term) => {
        courseMap[yearKey][term] = [];
      });
    }

    majorCoursesFromBackend.forEach((course) => {
      const isRequirement =
        course.courseType === 'Requirement' ||
        (course.courseType === 'Major Course' && course.Requirements !== '');
      const isElective = course.courseType === 'Elective';
      const isMajorCourse =
        course.courseType === 'Major Course' && course.Requirements === '';

      const shouldInclude =
        (dataType === 'Requirement' && isRequirement) ||
        (dataType === 'Elective' && isElective) ||
        (dataType === 'Major Course' && isMajorCourse);

      if (shouldInclude && course.courseYear <= maxMajorYears) {
        console.log('Semester Map: ', semesterMap);
        // Convert the stored semester value using the termMap.
        const semester = semesterMap[course.CourseSem] || course.CourseSem;
        courseMap[`year${course.courseYear}`][semester].push(course);
      }
    });

    return { [major]: courseMap };
  } catch (error) {
    console.error(`Error fetching ${dataType} courses:`, error);
    return null;
  }
};

const handleAutofillVariableCourses = async (
  selectedTerms,
  major,
  users,
  templateName,
  fetchedMajors,
  fetchedCourseData,
  termMap
) => {
  console.log('Term Map Gets here: ', termMap);
  
  // Get the major data for electives, requirements, and major courses using the dynamic termMap
  const fetchedMajorElectives = fetchMajorData(fetchedMajors, major, 'Elective', termMap);
  const fetchedMajorRequirements = fetchMajorData(
    fetchedMajors,
    major,
    'Requirement',
    termMap
  );
  const fetchedMajorCourses = fetchMajorData(
    fetchedMajors,
    major,
    'Major Course',
    termMap
  );
  
  // For this example, we'll use a fixed number of years (or this could be derived elsewhere)
  const numYears = 4;
  
  // Determine how many dynamic terms exist by using the keys of termMap.
  const termCount = Object.keys(termMap).length;
  
  // Create a dynamicTerms array from the termMap (sorted numerically by key)
  const dynamicTerms = Object.keys(termMap)
    .sort((a, b) => Number(a) - Number(b))
    .map(key => termMap[key]);
  
  const toastId = toast.loading(`Adding ${major} Pathway to selected students...`);

  try {
    for (const term of selectedTerms) {
      // Convert the global term index to a year and term name.
      // For example, if termCount is 3 and term is 4, then:
      // yearNumber = floor(4/3) + 1 = 2, termIndex = 4 % 3 = 1, so term = dynamicTerms[1]
      const yearNumber = Math.floor(term / termCount) + 1;
      const termIndex = term % termCount + 1;
      
      const semesterPromises = users.map((userId) =>
        handleAutofillCourse(
          fetchedCourseData,
          yearNumber,
          termIndex,
          userId,
          templateName,
          numYears,
          fetchedMajorElectives,
          fetchedMajorRequirements,
          fetchedMajorCourses,
          termMap
        )
      );

      await Promise.all(semesterPromises);
      console.log(`Completed autofill for ${termMap[termIndex + 1]} of year ${yearNumber}`);
    }

    toast.update(toastId, {
      render: `Successfully added the ${major} Pathway to selected students.`,
      type: 'success',
      isLoading: false,
      autoClose: 5000,
    });
  } catch (error) {
    toast.update(toastId, {
      render: 'Failed to autofill selected terms.',
      type: 'error',
      isLoading: false,
      autoClose: 5000,
    });
    console.error('Error autofilling courses:', error);
  }
};


const TemplateModal = ({
  isTemplateModalVisible,
  setIsTemplateModalVisible,
  selectedStudents,
}) => {
  const { fetchedMajors, fetchedTerms, fetchedCourseData } = useAuth();

  // Utility function to fix unquoted numeric keys in JSON
  function fixToJSON(str) {
    return str.replace(/(\{|,)\s*([0-9]+)\s*:/g, '$1 "$2":');
  }
  // Parse dynamic terms from fetchedTerms (e.g. {"1": "Fall", "2": "Spring", "3": "Summer"})
  const validJSONString = fixToJSON(fetchedTerms);
  const parsedTerms = JSON.parse(validJSONString);
  const termMap = {};
  Object.keys(parsedTerms).forEach((key) => {
    termMap[key] = parsedTerms[key];
  });
  // Get sorted term keys (as strings, sorted numerically)
  const sortedTermKeys = Object.keys(termMap).sort((a, b) => Number(a) - Number(b));
  // Create an array of dynamic term names (e.g. ['Fall', 'Spring', 'Summer'])
  const dynamicTerms = sortedTermKeys.map((key) => termMap[key]);
  console.log('Dynamic Terms: ', dynamicTerms);

  // For dynamic rendering, assume a default number of years (or you could derive this from user data)
  const defaultNumYears = 4;
  const totalTerms = defaultNumYears * dynamicTerms.length; // total buttons to display

  // State declarations
  const [isSemesterSelectionVisible, setIsSemesterSelectionVisible] = useState(false);
  const [selectedMajor, setSelectedMajor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMajors, setFilteredMajors] = useState(fetchedMajors || {});
  // We store the selected term as a global index (from 0 to totalTerms - 1)
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [isPrimary, setIsPrimary] = useState(true);

  useEffect(() => {
    if (fetchedMajors) {
      const filtered = Object.entries(fetchedMajors)
        .filter(([major]) =>
          major.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .reduce((acc, [major, value]) => {
          acc[major] = value;
          return acc;
        }, {});
      setFilteredMajors(filtered);
    }
  }, [searchQuery, fetchedMajors]);

  const handleMajorSelection = (major) => {
    setSelectedMajor(major);
    setIsSemesterSelectionVisible(true);
  };

  // Toggle selection: selectedTerms is an array of global term indices
  const toggleTermSelection = (globalIndex) => {
    if (selectedTerms.includes(globalIndex)) {
      setSelectedTerms((prev) => prev.filter((t) => t !== globalIndex));
    } else {
      setSelectedTerms((prev) => [...prev, globalIndex]);
    }
  };

  const handleCreatePlans = () => {
    if (selectedTerms.length > 0 && templateName.trim() !== '') {
      // Call the autofill function with the selected terms and other parameters.
      // The autofill function should then convert each global index into a year and term.
      handleAutofillVariableCourses(
        selectedTerms,
        selectedMajor,
        selectedStudents,
        templateName,
        fetchedMajors,
        fetchedCourseData,
        termMap,
        defaultNumYears
      );
      // Reset modal state after creation
      setIsTemplateModalVisible(false);
      setIsSemesterSelectionVisible(false);
      setSelectedMajor(null);
      setSelectedTerms([]);
      setTemplateName('');
      setIsPrimary(true);
    }
  };

  return (
    <Modal
      isVisible={isTemplateModalVisible}
      onClose={() => {
        setIsTemplateModalVisible(false);
        setIsSemesterSelectionVisible(false);
        setSelectedMajor(null);
        setSelectedTerms([]);
        setTemplateName('');
        setIsPrimary(true);
      }}
    >
      <div
        className={`flex flex-col bg-white rounded-md border border-gray-500 ${
          isSemesterSelectionVisible
            ? 'max-h-[80vh] min-w-[42vw] sm:w-[48rem]'
            : 'max-h-[70vh] min-w-[42vw] sm:w-[42rem]'
        }`}
      >
        {/* Header */}
        <div className="px-3 pt-3 pb-2 md:p-5 border-b dark:border-gray-600">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-700">
              {isSemesterSelectionVisible
                ? 'Review Plans to Create'
                : 'Select a Template'}
            </h3>
            <button
              type="button"
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
              onClick={() => {
                setIsTemplateModalVisible(false);
                setIsSemesterSelectionVisible(false);
                setSelectedMajor(null);
                setSelectedTerms([]);
                setTemplateName('');
                setIsPrimary(true);
              }}
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          {isSemesterSelectionVisible && (
            <div className="flex flex-wrap gap-2 mt-1">
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                {selectedStudents.length} Students Selected
              </span>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                Major: {selectedMajor}
              </span>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                {selectedTerms.length} Terms Selected
              </span>
            </div>
          )}
        </div>

        {/* Template Selection Screen */}
        {!isSemesterSelectionVisible && (
          <>
            <div className="px-3 py-2 border-b">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="grid grid-cols-1 gap-3">
                {Object.keys(filteredMajors).map((major) => (
                  <div
                    key={major}
                    className="p-2.5 border rounded text-left cursor-pointer hover:bg-gray-100"
                    onClick={() => handleMajorSelection(major)}
                  >
                    {major}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Semester Selection Screen */}
        {isSemesterSelectionVisible && (
          <div className="flex-1 overflow-y-auto px-3 py-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">
              Select Terms
            </h4>
            <div className="grid grid-cols-3 gap-2 mb-1">
              {Array.from({ length: totalTerms }).map((_, i) => {
                // Calculate the year number and term index from the global index
                const yearNumber = Math.floor(i / dynamicTerms.length) + 1;
                const termIndex = i % dynamicTerms.length;
                const termName = dynamicTerms[termIndex];
                const isSelected = selectedTerms.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() => toggleTermSelection(i)}
                    className={`relative py-2 px-3 border rounded font-semibold transition-colors duration-75 min-h-[70px] ${
                      isSelected ? 'bg-green-200' : 'bg-gray-50'
                    } hover:bg-green-100`}
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex justify-between items-start">
                        <span className="text-md font-bold">
                          {termName}
                        </span>
                        {isSelected && (
                          <div className="ml-2 w-4 h-4 bg-green-800 rounded-full flex items-center justify-center mt-1">
                            <FaCheck className="text-white" size={10} />
                          </div>
                        )}
                      </div>
                      <div className="mt-1 text-left">
                        <span className="inline-block py-0.5 px-2 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Major: {selectedMajor}
                        </span>
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                        {`Year ${yearNumber}`}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-gray-600 mb-3">
              Note: Only terms that exist in your term system will be added.
            </div>
            <div className="grid grid-cols-2 gap-4 mb-2">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-700 mb-1">
                  Choose whether to mark the created plans as primary
                </span>
                <div className="flex w-full mt-1 border border-gray-300 rounded-md overflow-hidden text-sm">
                  <button
                    className={`flex-1 text-center px-3 py-2 transition-colors duration-200 font-bold text-md ${
                      isPrimary
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => setIsPrimary(true)}
                  >
                    Mark these plans as primary
                  </button>
                  <button
                    className={`flex-1 text-center px-3 py-2 transition-colors duration-200 font-bold text-md ${
                      !isPrimary
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => setIsPrimary(false)}
                  >
                    Do not mark these plans as primary
                  </button>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-700 mb-1.5 mt-0.5">
                  Provide a name for the plans
                </span>
                <input
                  type="text"
                  placeholder="Enter plan name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 mb-2">
              <button
                type="button"
                className="text-gray-900 bg-gray-50 hover:bg-gray-100 border border-gray-300 focus:ring-4 focus:ring-gray-200 font-medium rounded-md text-sm px-3 py-2"
                onClick={() => {
                  setIsSemesterSelectionVisible(false);
                  setSelectedMajor(null);
                  setSelectedTerms([]);
                  setTemplateName('');
                  setIsPrimary(true);
                }}
              >
                &larr; Back
              </button>
              <button
                type="button"
                onClick={handleCreatePlans}
                disabled={selectedTerms.length === 0 || !templateName.trim()}
                className={`${
                  selectedTerms.length === 0 || !templateName.trim()
                    ? 'opacity-50 cursor-not-allowed bg-gray-50'
                    : 'hover:bg-blue-800 text-white'
                } text-gray-900 bg-blue-700 border border-gray-300 focus:ring-4 focus:ring-gray-200 font-medium rounded-md text-sm px-3 py-2`}
              >
                Create {selectedStudents.length} Plans
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TemplateModal;
